import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Loader2 } from "lucide-react";

// Ask Claude (through the connector) to rewrite one section's prose. Every other section and every figure stays.
export function RewriteSectionDialog({ open, onOpenChange, sections = [], initialSection = "intro", onStart, busy }) {
  const [section, setSection] = useState(initialSection);
  const [instruction, setInstruction] = useState("");
  const [tone, setTone] = useState("keep");
  useEffect(() => { if (open) { setSection(initialSection || "intro"); setInstruction(""); setTone("keep"); } }, [open, initialSection]);
  const TONES = [["keep", "Keep the tone"], ["brief", "Brief"], ["formal", "Formal"]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="rewrite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C99A00]" /> Rewrite one section via Claude</DialogTitle>
          <DialogDescription>Claude rewrites only the section you pick, through the connector, as you. The other twelve sections and every figure stay exactly as they are; the tie-out runs again when it lands.</DialogDescription>
        </DialogHeader>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">Section</span>
          <select value={section} onChange={(e) => setSection(e.target.value)} className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-2 py-2 bg-white focus:border-[#003E87] outline-none" data-testid="rewrite-section">
            {sections.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">What should change <span className="normal-case text-slate-400">(optional)</span></span>
          <Textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={4} className="mt-1 text-sm" placeholder="e.g. Shorter. Lead with the pile production rate. Mention the laydown yard is the client's." data-testid="rewrite-instruction" />
        </label>
        <div>
          <span className="text-[11px] uppercase tracking-wide text-slate-500">Tone</span>
          <div className="mt-1 inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50" role="radiogroup" data-testid="rewrite-tone">
            {TONES.map(([k, lbl]) => (
              <button key={k} type="button" role="radio" aria-checked={tone === k} onClick={() => setTone(k)} data-testid={`rewrite-tone-${k}`}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${tone === k ? "bg-[#003E87] text-white" : "text-slate-600 hover:text-slate-900"}`}>{lbl}</button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">{tone === "brief" ? "The fewest words that carry the facts; one short sentence per item." : tone === "formal" ? "Full sentences, no contractions, measured and precise." : "Claude keeps the register the section already has."}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rewrite-cancel">Cancel</Button>
          <Button onClick={() => onStart(section, instruction, tone)} disabled={busy} className="bg-[#F4C400] text-black hover:brightness-95" data-testid="rewrite-start">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Ask Claude
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
